import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Button } from 'components/Button';
import { Toggle } from 'components/Toggle';
import { withNotification } from 'components/Notification';
import { useStore } from 'context/store-context';
import { UPDATE_STORE } from 'gql/mutations';
import { NotificationTypes } from 'utils';

type Field = 'checkoutPlus' | 'addToCartPlus';
type Configuration = Record<string, unknown>;
type Configurations = Record<Field, Configuration>;
const labels: Record<Field, string> = { checkoutPlus: 'Checkout+', addToCartPlus: 'Add to Cart+' };
const fields: Field[] = ['checkoutPlus', 'addToCartPlus'];
const configuration = (value: unknown): Configuration =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Configuration : {};
const selectors = (value: Configuration): string[] => Array.isArray(value.selectors)
  ? value.selectors.filter((item): item is string => typeof item === 'string') : [];
const selectorText = (values: Configurations) => ({
  checkoutPlus: selectors(values.checkoutPlus).join('\n'),
  addToCartPlus: selectors(values.addToCartPlus).join('\n'),
});

function ConfigurationEditor({ disabled, showNotification }: {
  disabled: boolean;
  showNotification: (...args: any[]) => void;
}) {
  const { storeId, storeProperties, storeLoading } = useStore();
  const initial = { checkoutPlus: configuration(storeProperties?.checkoutPlus),
    addToCartPlus: configuration(storeProperties?.addToCartPlus) };
  const [confirmed, setConfirmed] = useState<Configurations>(initial);
  const lastConfirmed = useRef<Configurations>(initial);
  const [edits, setEdits] = useState(selectorText(initial));
  const [saving, setSaving] = useState<Field | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [updateStore] = useMutation(UPDATE_STORE);
  const inFlight = useRef(false);
  const generation = useRef(0);
  const ready = Boolean(storeId && !storeLoading && storeProperties?.id === storeId);
  const gate = useRef({ disabled, ready }); gate.current = { disabled, ready };

  useEffect(() => {
    const next = { checkoutPlus: configuration(storeProperties?.checkoutPlus),
      addToCartPlus: configuration(storeProperties?.addToCartPlus) };
    const before = selectorText(lastConfirmed.current);
    const after = selectorText(next);
    setConfirmed(next);
    setEdits(previous => ({
      checkoutPlus: previous.checkoutPlus === before.checkoutPlus ? after.checkoutPlus : previous.checkoutPlus,
      addToCartPlus: previous.addToCartPlus === before.addToCartPlus ? after.addToCartPlus : previous.addToCartPlus,
    }));
    lastConfirmed.current = next;
  }, [storeProperties?.checkoutPlus, storeProperties?.addToCartPlus]);
  useEffect(() => () => { generation.current++; inFlight.current = false; }, []);

  const save = (field: Field, next: Configuration, savingSelectors = false) => {
    if (gate.current.disabled || !gate.current.ready || inFlight.current) return;
    inFlight.current = true;
    const request = ++generation.current;
    let settled = false;
    setSaving(field); setError(''); setNotice('');
    const finish = () => {
      if (request !== generation.current || settled) return false;
      settled = true; inFlight.current = false; setSaving(null); return true;
    };
    const fail = () => {
      if (!finish()) return;
      const message = 'The configuration could not be saved. Your last confirmed settings are kept. Try again.';
      setError(message); showNotification(NotificationTypes.error, message);
    };
    try {
      const result = updateStore({
        variables: { storeId, set: { [field]: next } },
        onCompleted(data) {
          if (!finish()) return;
          if (data?.updateStore?.id !== storeId) {
            const message = 'The configuration update could not be confirmed. Your last confirmed settings are kept.';
            setError(message); showNotification(NotificationTypes.error, message); return;
          }
          setConfirmed(previous => ({ ...previous, [field]: next }));
          lastConfirmed.current = { ...lastConfirmed.current, [field]: next };
          if (savingSelectors) setEdits(previous => ({ ...previous, [field]: selectors(next).join('\n') }));
          const message = `${labels[field]} configuration saved.`;
          setNotice(message); showNotification(NotificationTypes.success, message);
        },
        onError: fail,
      });
      if (result && typeof result.catch === 'function') void result.catch(fail);
    } catch { fail(); }
  };
  const blocked = disabled || !ready || saving !== null;

  return <div className="mt-4 space-y-4">
    <p className="text-sm text-slate-600">Changes here update your current store configuration immediately. They are saved separately from your installation draft.</p>
    {disabled && <p className="text-xs text-amber-800">These controls are unavailable while your installation draft is being edited or your account has read-only access.</p>}
    {!ready && <p role="status" className="text-sm text-slate-600">Wait for the selected store’s settings to load.</p>}
    {fields.map(field => {
      const enabled = ready && confirmed[field].enabled === true;
      const locked = field === 'checkoutPlus' && enabled;
      const parsedSelectors = Array.from(new Set(edits[field].split('\n').map(value => value.trim()).filter(Boolean)));
      const selectorsChanged = JSON.stringify(parsedSelectors) !== JSON.stringify(selectors(confirmed[field]));
      return <section key={field} aria-label={`${labels[field]} live configuration`} className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between gap-3">
          <div><h4 className="text-sm font-semibold text-slate-900">{labels[field]}</h4>
            <p className="mt-1 text-xs text-slate-500">{field === 'checkoutPlus' ? 'Cart checkout controls' : 'Product-page guarantee option'}</p></div>
          <Toggle ariaLabel={`${labels[field]} configuration`} isEnabled={enabled} disabled={blocked || locked}
            onChange={value => { if (!locked) save(field, { ...confirmed[field], enabled: value }); }} />
        </div>
        <p className="mt-2 text-xs text-slate-600">Saved configuration: {enabled ? 'Enabled' : 'Disabled'}</p>
        {locked && <p className="mt-2 text-xs text-slate-500">Contact SHIPAID to change an active Checkout+ installation.</p>}
        <label className="mt-4 block text-xs font-medium text-slate-700">{labels[field]} selectors
          <textarea rows={2} disabled={blocked} value={edits[field]}
            className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:opacity-50"
            onChange={event => {
              const value = event.target.value;
              setEdits(previous => ({ ...previous, [field]: value }));
            }} />
        </label>
        <p className="mt-1 text-xs text-slate-500">One selector per line. Change these only with your developer or SHIPAID team.</p>
        <p className="mt-2 break-all text-xs text-slate-600">Saved selectors: {selectors(confirmed[field]).join(', ') || 'None'}</p>
        <div className="mt-3"><Button variant="outlined" disabled={blocked || !selectorsChanged}
          onClick={() => save(field, { ...confirmed[field], selectors: parsedSelectors }, true)}>Save {labels[field]} selectors</Button></div>
        {saving === field && <p role="status" className="mt-2 text-xs text-slate-600">Saving…</p>}
      </section>;
    })}
    {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}
    {notice && <p role="status" className="text-sm text-green-800">{notice}</p>}
    <p className="text-xs text-slate-500">Configuration does not verify placement, cart behavior or checkout. Complete the storefront check after any change.</p>
  </div>;
}

const ExistingInstallationControls = withNotification(({ disabled = false, showNotification }: {
  disabled?: boolean;
  showNotification: (...args: any[]) => void;
}) => {
  const { storeId, storeProperties } = useStore();
  return <details className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
    <summary className="min-h-[44px] cursor-pointer text-sm font-semibold text-slate-800">Advanced installation controls</summary>
    <ConfigurationEditor key={`${storeId}:${storeProperties?.store ?? ''}`} disabled={disabled} showNotification={showNotification} />
  </details>;
});

export default ExistingInstallationControls;
